import {
  isJobRoleToken,
  isOrgRoleToken,
} from '@/utils/internalEmailRecipients';
import type { RecipientOption } from './useRecipientSections';

/**
 * How many people the rows in a recipient field add up to, so the picker can stop
 * offering more once a budget is spent.
 *
 * **This over-counts, by design.** The backend resolves roles to people, dedupes
 * them and subtracts To from cc/bcc before it counts; the browser can do the first
 * of those and only partly. A Kula role reaches us as a number with no ids
 * (`members_count` on `/org_roles`), so someone picked individually who also holds
 * that role is two here and one there. The error is always in the same direction —
 * stricter, never looser — and a send is still refused server-side, so the worst
 * case is the menu closing a row slightly early rather than letting one through.
 *
 * Job roles do carry their members' ids (the hiring team response), so those are
 * deduped exactly against individual picks and against each other.
 */
export interface RecipientCostModel {
  /** Job role token -> the ids of the people holding it on this job. */
  memberIdsByJobRole: Map<string, string[]>;
  /** Kula role token -> `members_count`. No ids exist to dedupe by. */
  memberCountByOrgRole: Map<string, number>;
}

export const EMPTY_COST_MODEL: RecipientCostModel = {
  memberIdsByJobRole: new Map(),
  memberCountByOrgRole: new Map(),
};

export interface RecipientTally {
  /** Distinct people: individual picks unioned with job-role members. */
  userIds: Set<string>;
  /** Kula-role members, which have no ids and so can only be added up. */
  roleMembers: number;
  /** The tokens folded in, so a row already counted is not counted twice. */
  tokens: Set<string>;
  total: number;
}

/** Builds the model from the option lists, which must be the unfiltered ones —
 *  the menu's sections are narrowed by the search box, and a row that is selected
 *  but filtered out still has to carry its weight. */
export const buildCostModel = (
  roleOptions: RecipientOption[] = []
): RecipientCostModel => {
  const memberIdsByJobRole = new Map<string, string[]>();
  const memberCountByOrgRole = new Map<string, number>();

  roleOptions.forEach((option) => {
    if (option.memberIds)
      memberIdsByJobRole.set(option.value, option.memberIds);
    else if (typeof option.memberCount === 'number') {
      memberCountByOrgRole.set(option.value, option.memberCount);
    }
  });

  return { memberIdsByJobRole, memberCountByOrgRole };
};

export const tallyRecipients = (
  tokens: string[] = [],
  model: RecipientCostModel = EMPTY_COST_MODEL
): RecipientTally => {
  const userIds = new Set<string>();
  const seen = new Set<string>();
  let roleMembers = 0;

  tokens.forEach((token) => {
    if (!token || seen.has(token)) return;
    seen.add(token);

    if (isOrgRoleToken(token)) {
      roleMembers += model.memberCountByOrgRole.get(token) ?? 1;
      return;
    }

    if (isJobRoleToken(token)) {
      // Unknown until the hiring team lands. One is the honest floor — a role
      // always reaches at least somebody, or it would have been disabled.
      const members = model.memberIdsByJobRole.get(token);
      if (!members) {
        roleMembers += 1;
        return;
      }
      members.forEach((id) => userIds.add(id));
      return;
    }

    userIds.add(token);
  });

  return {
    userIds,
    roleMembers,
    tokens: seen,
    total: userIds.size + roleMembers,
  };
};

/**
 * What ticking `token` would add on top of `tally` — zero when it reaches nobody
 * new, which is what lets a person a selected job role already covers still be
 * picked with no budget left.
 */
export const costOfAdding = (
  token: string,
  tally: RecipientTally,
  model: RecipientCostModel = EMPTY_COST_MODEL
): number => {
  if (!token || tally.tokens.has(token)) return 0;

  if (isOrgRoleToken(token)) {
    return model.memberCountByOrgRole.get(token) ?? 1;
  }

  if (isJobRoleToken(token)) {
    const members = model.memberIdsByJobRole.get(token);
    if (!members) return 1;
    return members.filter((id) => !tally.userIds.has(id)).length;
  }

  return tally.userIds.has(token) ? 0 : 1;
};

/** The row is greyed out because there is no room left at all. */
export const RECIPIENT_LIMIT_HINT = 'Recipient limit reached';

/** The row is greyed out because this particular role is bigger than the room left. */
export const RECIPIENT_OVER_LIMIT_HINT = 'More people than the limit allows';
